import type { WorkDay, WorkDayCreateRequest, WorkDayDetailsRequest, WorkDayListQuery } from "@shyne/shared/api";
import { ReadAllResponse, timestamp, uuid } from "@/interfaces";
import { activeOrganizationId, activeOrganizationIdIfAny } from "@/lib/active-organization";
import { api } from "@/lib/api";
import {
  daily_work_status,
  daily_work_day_status,
  daily_work_log_type,
  daily_work_hours_source,
  daily_work_payout_review,
  DailyWorkLog,
} from "@/models/daily-work.model.ts";
import { toContractCreateRequest, toContractDetailsRequest, toContractListQuery, toContractRecord, type ContractDetailsChange, type ContractListFilters, type NewContractRecord } from "@/repositories/contract-records";
import { clockRecordsOf, toContractLogRecord, toWorkDayListQuery, toWorkDayRecord, type WorkDayListFilters, type WorkDayPageRequest } from "@/repositories/work-day-records";
import { individual_payment_status, subs_payment_status } from "@/models/appointment.model";
import { requirePermission } from "@/lib/permissions";
import type { DailyWorkExecutionJoin } from "@/repositories/daily-work-execution.repository";

export interface DailyWorkJoin {
  id: uuid;
  vendor: {
    id: uuid;
    display_name: string;
  };
  property: {
    id: uuid;
    name: string;
    address: string | null;
    image: string | null;
  };
  status: daily_work_status;
  start_date: string | null;
  end_date: string | null;
  monthly_price: number | null;
  created_at: string;
}

export type ContractOfDay = Omit<DailyWorkJoin, "monthly_price" | "created_at" | "status">;

type PaySource = "profile" | "manual" | "none";

export interface DailyWorkDayJoin {
  id: uuid;
  daily_work: uuid;
  work_date: string;
  status: daily_work_day_status;
  service: { id: uuid; name: string; parent_name: string };
  tasks: string[];
  assigned_user: { id: uuid; name: string } | null;
  assigned_org: { id: uuid; display_name: string } | null;
  individual_payment: number | null;
  individual_payment_total: number | null;
  individual_payment_status: individual_payment_status | null;
  individual_payment_source: PaySource | null;
  subs_payment: number | null;
  subs_payment_total: number | null;
  subs_payment_status: subs_payment_status | null;
  subs_payment_source: PaySource | null;
  max_hours: number | null;
  duration_label: string | null;
  instructions: string | null;
  recurring: boolean;
  schedule_detached_at: string | null;
  hours_worked: number | null;
  hours_source: daily_work_hours_source | null;
  payout_review: daily_work_payout_review | null;
  completed_at: timestamp | null;
  created_at: timestamp | null;
  updated_at: timestamp | null;
  daily_work_data: ContractOfDay;
}

export interface DailyWorkLogJoin extends Omit<DailyWorkLog, "user" | "type"> {
  type: daily_work_log_type | null;
  user?: { id: uuid; name: string } | null;
}

export type DayWithClockRecords = { day: DailyWorkDayJoin; clockRecords: DailyWorkExecutionJoin[] };

const withClockRecords = (day: WorkDay): DayWithClockRecords => ({ day: toWorkDayRecord(day), clockRecords: clockRecordsOf(day) });

const WORK_DAY_PAGE = 1000;

async function everyWorkDay(query: Partial<WorkDayListQuery>): Promise<DailyWorkDayJoin[]> {
  const days: DailyWorkDayJoin[] = [];
  for (let page = 1; ; page++) {
    const { items, total } = await api.workDays.list({ ...query, page, pageSize: WORK_DAY_PAGE });
    days.push(...items.map(toWorkDayRecord));
    if (items.length < WORK_DAY_PAGE || days.length >= total) return days;
  }
}

async function workDayPage(filters: WorkDayListFilters, page: WorkDayPageRequest): Promise<ReadAllResponse<DailyWorkDayJoin>> {
  const { items, total } = await api.workDays.list(toWorkDayListQuery(filters, page));
  return { data: items.map(toWorkDayRecord), count: total };
}

export const DailyWorkRepository = {
  async getById(id: uuid): Promise<DailyWorkJoin> {
    return toContractRecord(await api.contracts.get(id, { actingOrganization: activeOrganizationId() }));
  },

  async listForVendor(
    page = 1,
    pageSize = 10,
    filters: ContractListFilters,
    sortKey: string = "created_at",
    sortOrder: "asc" | "desc" = "desc"
  ): Promise<ReadAllResponse<DailyWorkJoin>> {
    const query = toContractListQuery(filters, { page, pageSize, sortKey, sortOrder, actingOrganization: activeOrganizationId() });
    const { items, total } = await api.contracts.list(query);
    return { data: items.map(toContractRecord), count: total };
  },

  async countForVendor(status?: daily_work_status): Promise<number> {
    const { total } = await api.contracts.list({ actingOrganization: activeOrganizationId(), status, pageSize: 1 });
    return total;
  },

  async create(payload: NewContractRecord): Promise<DailyWorkJoin> {
    await requirePermission("daily_work.create");

    return toContractRecord(await api.contracts.create(toContractCreateRequest(payload)));
  },

  async update(id: uuid, payload: ContractDetailsChange): Promise<DailyWorkJoin> {
    await requirePermission("daily_work.update");

    return toContractRecord(await api.contracts.setDetails(id, toContractDetailsRequest(payload, activeOrganizationId())));
  },

  async listLogs(id: uuid): Promise<DailyWorkLogJoin[]> {
    await requirePermission("daily_work.view");

    const { items } = await api.contracts.history(id, { actingOrganization: activeOrganizationId() });
    return items.map((entry) => toContractLogRecord(id, entry));
  },

  async getDayWithClockRecords(dayId: uuid): Promise<DayWithClockRecords> {
    await requirePermission("daily_work.view");

    return withClockRecords(await api.workDays.get(dayId, { actingOrganization: activeOrganizationIdIfAny() }));
  },

  async getDaysByDailyWork(dailyWorkId: uuid, startDate?: string, endDate?: string): Promise<DailyWorkDayJoin[]> {
    await requirePermission("daily_work.view");

    return everyWorkDay(toWorkDayListQuery({ contract: dailyWorkId, startDate, endDate }, { page: 1, pageSize: WORK_DAY_PAGE, actingOrganization: activeOrganizationId() }));
  },

  async listDaysForSubs(
    page = 1,
    pageSize = 10,
    filters: WorkDayListFilters,
    sortKey: string = "work_date",
    sortOrder: "asc" | "desc" = "asc"
  ): Promise<ReadAllResponse<DailyWorkDayJoin>> {
    await requirePermission("daily_work.view");

    return workDayPage(filters, { page, pageSize, sortKey, sortOrder, actingOrganization: activeOrganizationId() });
  },

  async listDaysForIndividual(
    page = 1,
    pageSize = 10,
    filters: WorkDayListFilters,
    sortKey: string = "work_date",
    sortOrder: "asc" | "desc" = "asc"
  ): Promise<ReadAllResponse<DailyWorkDayJoin>> {
    await requirePermission("daily_work.view");

    return workDayPage({ ...filters, assignedToMe: true }, { page, pageSize, sortKey, sortOrder, actingOrganization: activeOrganizationIdIfAny() });
  },

  async listDaysForVendor(page = 1, pageSize = 500, filters: WorkDayListFilters): Promise<ReadAllResponse<DailyWorkDayJoin>> {
    await requirePermission("daily_work.view");

    return workDayPage(filters, { page, pageSize, actingOrganization: activeOrganizationId() });
  },

  async addDay(contract: uuid, day: Omit<WorkDayCreateRequest, "actingOrganization">): Promise<DailyWorkDayJoin> {
    await requirePermission("daily_work.update");

    return toWorkDayRecord(await api.contracts.addDay(contract, { ...day, actingOrganization: activeOrganizationId() }));
  },

  async changeDay(id: uuid, change: Omit<WorkDayDetailsRequest, "actingOrganization">): Promise<DailyWorkDayJoin> {
    await requirePermission("daily_work.update");

    return toWorkDayRecord(await api.workDays.setDetails(id, { ...change, actingOrganization: activeOrganizationId() }));
  },

  async resolvePayoutHours(id: uuid, hoursWorked: number): Promise<DailyWorkDayJoin> {
    await requirePermission("daily_work.update");

    return toWorkDayRecord(await api.workDays.approvePayoutHours(id, { hours: hoursWorked, actingOrganization: activeOrganizationId() }));
  },

  async markOffSite(id: uuid, { clockOut, note }: { clockOut: string; note: string | null }): Promise<DayWithClockRecords> {
    await requirePermission("daily_work.update");

    return withClockRecords(await api.workDays.markOffSite(id, { offSiteTime: clockOut, note, actingOrganization: activeOrganizationId() }));
  },

  async acceptDay(dayId: uuid): Promise<DailyWorkDayJoin> {
    await requirePermission("daily_work.update");

    return toWorkDayRecord(await api.workDays.accept(dayId, { actingOrganization: activeOrganizationIdIfAny() }));
  },

  async goOnSite(dayId: uuid): Promise<DayWithClockRecords> {
    await requirePermission("daily_work.update");

    return withClockRecords(await api.workDays.clockIn(dayId, { actingOrganization: activeOrganizationIdIfAny() }));
  },

  async goOffSite(dayId: uuid, notes: string): Promise<DayWithClockRecords> {
    await requirePermission("daily_work.update");

    return withClockRecords(await api.workDays.clockOut(dayId, { notes, actingOrganization: activeOrganizationIdIfAny() }));
  },

  async tickTask(dayId: uuid, position: number, done: boolean): Promise<DayWithClockRecords> {
    await requirePermission("daily_work.update");

    return withClockRecords(await api.workDays.tickTask(dayId, position, { done, actingOrganization: activeOrganizationIdIfAny() }));
  },

  async writeNotes(dayId: uuid, notes: string): Promise<DayWithClockRecords> {
    await requirePermission("daily_work.update");

    return withClockRecords(await api.workDays.writeNotes(dayId, { notes, actingOrganization: activeOrganizationIdIfAny() }));
  },

  async assignOwnWorker(dayId: uuid, worker: uuid, rate: number): Promise<DailyWorkDayJoin> {
    await requirePermission("daily_work.update");

    return toWorkDayRecord(await api.workDays.assignWorker(dayId, { worker, rate, actingOrganization: activeOrganizationId() }));
  },

  async unassignOwnWorker(dayId: uuid): Promise<DailyWorkDayJoin> {
    await requirePermission("daily_work.update");

    return toWorkDayRecord(await api.workDays.unassignWorker(dayId, { actingOrganization: activeOrganizationId() }));
  },

  async deleteDay(id: uuid): Promise<void> {
    await requirePermission("daily_work.update");

    await api.workDays.delete(id, { actingOrganization: activeOrganizationId() });
  },
};
